'use client';

import { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * Reusable Drawer component that slides in from specified direction
 * Features smooth open/close animations with proper easing
 */
export function Drawer({
  isOpen,
  onClose,
  children,
  direction = 'bottom',
  className = '',
  showOverlay = true,
  closeOnOverlayClick = true,
  customMobileTiming = null,
  // Optional: custom timing for mobile (bottom/top)
  customDesktopTiming = null // Optional: custom timing for desktop (left/right)
}) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const timeoutRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Track initial mount
  useEffect(() => {
    setIsInitialMount(false);
  }, []);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Prevent iOS bounce/overscroll during animation
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.overscrollBehavior = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.overscrollBehavior = '';
    }
  }, [isOpen]);

  // Escape key handler
  useEffect(() => {
    const handleEscape = e => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Transform values for slide animations
  const getTransform = isVisible => {
    switch (direction) {
      case 'bottom':
        return isVisible ? 'translateY(0)' : 'translateY(100%)';
      case 'right':
        return isVisible ? 'translateX(0)' : 'translateX(100%)';
      case 'left':
        return isVisible ? 'translateX(0)' : 'translateX(-100%)';
      case 'top':
        return isVisible ? 'translateY(0)' : 'translateY(-100%)';
      default:
        return isVisible ? 'translateY(0)' : 'translateY(100%)';
    }
  };

  // Different timing for mobile (bottom) vs desktop (right/left)
  const getTransitionTiming = () => {
    const isMobileDirection = direction === 'bottom' || direction === 'top';

    // Use custom timing if provided
    if (isMobileDirection && customMobileTiming) {
      return customMobileTiming;
    }
    if (!isMobileDirection && customDesktopTiming) {
      return customDesktopTiming;
    }

    // Default timing
    if (isMobileDirection) {
      // Mobile: slightly longer, smoother easing
      return 'transform 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    }
    // Desktop: standard Material Design easing
    return 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)';
  };

  // Extract duration from transition timing for cleanup timeout
  const getTransitionDuration = () => {
    const timing = getTransitionTiming();
    const match = timing.match(/(\d+)ms/);
    return match ? parseInt(match[1], 10) : 350;
  };

  // Get overlay transition timing
  const getOverlayTiming = () => {
    const duration = getTransitionDuration();
    const isMobileDirection = direction === 'bottom' || direction === 'top';
    const easing = isMobileDirection ? 'ease-out' : 'ease-in-out';
    return `opacity ${duration}ms ${easing}`;
  };

  // Animation state management with proper cleanup
  useEffect(() => {
    // Don't run if neither isOpen nor shouldRender changed
    if (!isOpen && !shouldRender) return;

    // Clear any pending timeouts/animations
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (isOpen && !shouldRender) {
      // Step 1: Render the drawer (off-screen)
      setShouldRender(true);
    } else if (isOpen && shouldRender && !isAnimating) {
      // Step 2: Trigger animation after render (only when shouldRender just became true)
      // Extra delay on initial mount ensures styles are fully applied after hard refresh
      const delay = isInitialMount ? 30 : 10;
      timeoutRef.current = setTimeout(() => {
        setIsAnimating(true);
      }, delay);
    } else if (!isOpen && shouldRender) {
      // Trigger close animation
      setIsAnimating(false);

      // Remove from DOM after animation completes
      const duration = getTransitionDuration();
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        setIsAnimating(false); // Reset animation state
      }, duration + 50);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, shouldRender]);
  if (!shouldRender) return null;
  const positions = {
    bottom: 'inset-x-0 bottom-0',
    right: 'inset-y-0 right-0',
    left: 'inset-y-0 left-0',
    top: 'inset-x-0 top-0'
  };
  const drawerContent = <div className="fixed inset-0 z-50">
      {showOverlay && <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" style={{
      opacity: isAnimating ? 1 : 0,
      transition: getOverlayTiming()
    }} onClick={closeOnOverlayClick ? onClose : undefined} aria-hidden="true" />}
      <div className={`fixed bg-white shadow-xl ${positions[direction]} ${className}`} style={{
      transform: getTransform(isAnimating),
      transition: getTransitionTiming(),
      willChange: 'transform',
      WebkitBackfaceVisibility: 'hidden',
      backfaceVisibility: 'hidden',
      WebkitPerspective: 1000,
      perspective: 1000,
      touchAction: 'pan-y',
      WebkitOverflowScrolling: 'touch',
      overscrollBehavior: 'contain'
    }}>
        {children}
      </div>
    </div>;
  return typeof window !== 'undefined' && document.body ? ReactDOM.createPortal(drawerContent, document.body) : drawerContent;
}